import { QUALITY_LABELS, type GrabStatus, type MediaState, type QualityKey } from '@jelly-dl/shared';

export function humanBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function timeAgo(ms: number | null): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function formatDateTime(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString();
}

/** Color classes for the media lifecycle state (movie / season / episode). */
export const MEDIA_STATE_COLORS: Record<MediaState, string> = {
  unmonitored: 'bg-slate-700 text-slate-300',
  wanted: 'bg-amber-600/30 text-amber-300 border border-amber-600/40',
  searching: 'bg-sky-600/30 text-sky-300 border border-sky-600/40',
  grabbed: 'bg-indigo-600/30 text-indigo-300 border border-indigo-600/40',
  downloading: 'bg-blue-600/30 text-blue-300 border border-blue-600/40',
  importing: 'bg-violet-600/30 text-violet-300 border border-violet-600/40',
  imported: 'bg-emerald-600/30 text-emerald-300 border border-emerald-600/40',
  failed: 'bg-rose-600/30 text-rose-300 border border-rose-600/40',
};

export const GRAB_STATUS_COLORS: Record<GrabStatus, string> = {
  queued: 'text-slate-300',
  downloading: 'text-blue-300',
  postprocessing: 'text-sky-300',
  downloaded: 'text-cyan-300',
  importing: 'text-violet-300',
  imported: 'text-emerald-300',
  failed: 'text-rose-300',
  cancelled: 'text-slate-400',
};

/** Badge color per video quality tier, grouped by resolution. */
export const QUALITY_COLORS: Record<QualityKey, string> = {
  '2160p-bluray': 'bg-fuchsia-600/30 text-fuchsia-200',
  '2160p-webdl': 'bg-fuchsia-600/20 text-fuchsia-200',
  '2160p-webrip': 'bg-fuchsia-600/20 text-fuchsia-300',
  '1080p-bluray': 'bg-emerald-600/30 text-emerald-200',
  '1080p-webdl': 'bg-emerald-600/20 text-emerald-200',
  '1080p-webrip': 'bg-emerald-600/20 text-emerald-300',
  '1080p-hdtv': 'bg-teal-600/20 text-teal-200',
  '720p-bluray': 'bg-sky-600/30 text-sky-200',
  '720p-webdl': 'bg-sky-600/20 text-sky-200',
  '720p-webrip': 'bg-sky-600/20 text-sky-300',
  '720p-hdtv': 'bg-cyan-600/20 text-cyan-200',
  '480p-dvd': 'bg-slate-600/40 text-slate-200',
  '480p-hdtv': 'bg-slate-600/40 text-slate-200',
  sd: 'bg-slate-700 text-slate-300',
  unknown: 'bg-slate-700 text-slate-400',
};

/** Quality badge text via the shared label map. */
export function qualityLabel(key: QualityKey): string {
  return QUALITY_LABELS[key];
}
